import React from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const savedError = sessionStorage.getItem('app_error');
    this.state = {
      hasError: !!savedError,
      error: savedError ? new Error(savedError) : null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Save error to session storage
    sessionStorage.setItem('app_error', error.message);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleRefresh = () => {
    // Clear error from session storage
    sessionStorage.removeItem('app_error');
    // Clear app state
    localStorage.clear();
    // Force reload the page
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 bg-black flex items-center justify-center p-4 safe-area-top safe-area-bottom">
          <div className="bg-gray-900 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-center mb-4">
              <AlertTriangle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-white text-center mb-2">
              Oops! Something went wrong
            </h1>
            <p className="text-gray-400 text-center mb-4">
              We're sorry, but something unexpected happened. The app will be reset to recover from this error.
            </p>
            {this.state.error && (
              <div className="bg-gray-800 rounded p-3 mb-4 overflow-auto">
                <pre className="text-red-400 text-sm whitespace-pre-wrap break-words">
                  {this.state.error.message}
                </pre>
              </div>
            )}
            <div className="flex justify-center">
              <button
                onClick={this.handleRefresh}
                className="bg-blue-600 text-white px-6 py-3 rounded-full hover:bg-blue-500 transition-colors text-lg font-medium"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}